import {
  ENTITIES_PAGE_ID,
  isLegacyInheritingDataPageId,
} from '../../data-models/dataModelsConstants';
import { isOmniChatChildId, OMNICHAT_PAGE_ID, OMNICHAT_PAGE_IDS } from '../../omnichat/omniChatConstants';

/** Minimal shape required for privilege merge (full RoutePrivilege is a superset). */
export interface RoutePrivilegeNode {
  id: string;
  hasAccess: boolean;
  children?: RoutePrivilegeNode[];
}

function indexHasAccessById(routes: RoutePrivilegeNode[]): Map<string, boolean> {
  const map = new Map<string, boolean>();

  const walk = (nodes: RoutePrivilegeNode[]) => {
    for (const node of nodes) {
      map.set(node.id, node.hasAccess);
      if (node.children?.length) walk(node.children);
    }
  };

  walk(routes);
  return map;
}

function resolveHasAccess(id: string, stored: Map<string, boolean>): boolean | undefined {
  if (stored.has(id)) return stored.get(id);
  if (isLegacyInheritingDataPageId(id) && stored.has(ENTITIES_PAGE_ID)) {
    return stored.get(ENTITIES_PAGE_ID);
  }
  if (isOmniChatChildId(id) && stored.has(OMNICHAT_PAGE_ID)) {
    return stored.get(OMNICHAT_PAGE_ID);
  }
  return undefined;
}

export function grantRouteIds<T extends RoutePrivilegeNode>(routes: T[], ids: ReadonlySet<string>): T[] {
  return routes.map((node) => ({
    ...node,
    hasAccess: ids.has(node.id) ? true : node.hasAccess,
    children: node.children?.length ? grantRouteIds(node.children as T[], ids) : node.children,
  }));
}

export function grantOmniChatIfCurrentUser<T extends RoutePrivilegeNode>(userId: string, routes: T[]): T[] {
  if (typeof localStorage === 'undefined' || localStorage.getItem('user_uid') !== userId) return routes;
  return grantRouteIds(routes, new Set<string>(OMNICHAT_PAGE_IDS));
}

/**
 * Always uses template for structure. Overlays hasAccess from DB by id.
 * Routes present only in the template (not in DB) get hasAccess: false,
 * except Data Models / Data Ingestion which inherit the Entities grant until saved.
 * Routes present only in DB are dropped.
 */
export function mergeTemplateWithDb<T extends RoutePrivilegeNode>(
  template: T[],
  dbRoutes: RoutePrivilegeNode[]
): T[] {
  const dbAccess = indexHasAccessById(dbRoutes);

  const apply = (nodes: T[]): T[] =>
    nodes.map((node) => ({
      ...node,
      hasAccess: resolveHasAccess(node.id, dbAccess) ?? false,
      children: node.children?.length
        ? apply(node.children as T[])
        : node.children,
    }));

  return apply(template);
}

/** Template structure with hasAccess = role OR user grant. */
export function mergeRoleAndUserPrivileges<T extends RoutePrivilegeNode>(
  template: T[],
  roleRoutes: RoutePrivilegeNode[],
  userRoutes: RoutePrivilegeNode[]
): T[] {
  const roleAccess = indexHasAccessById(roleRoutes);
  const userAccess = indexHasAccessById(userRoutes);

  const apply = (nodes: T[]): T[] =>
    nodes.map((node) => {
      const stored =
        roleAccess.has(node.id) || userAccess.has(node.id)
          ? !!(roleAccess.get(node.id) || userAccess.get(node.id))
          : !!(
              resolveHasAccess(node.id, roleAccess) ||
              resolveHasAccess(node.id, userAccess)
            );
      return {
        ...node,
        hasAccess: stored,
        children: node.children?.length
          ? apply(node.children as T[])
          : node.children,
      };
    });

  return apply(template);
}
